<template>
  <div>
    <div id="production-table">
      <div class="table-header">
        <v-chip
          v-if="['sho@plykea.com', 'kath@plykea.com', 'adam@plykea.com'].includes($store.state.user.email)"
          class="text-body-2 mr-4"
          color="green"
          >{{ ukOrderTally }}</v-chip
        >
        <v-text-field
          v-model="tableSearch"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
          :clearable="true"
        ></v-text-field>
      </div>
      <div id="dispatchedTable">
        <v-data-table
          :items="shipments"
          :headers="headers"
          :search="tableSearch"
          :loading="loading"
          :no-data-text="noDataText"
          :sort-by="[{ key: 'dateScheduled', order: 'desc' }]"
        >
          <template v-slot:item="props">
            <tr>
              <td>{{ props.item.orderNumber || props.item.invoiceNumber }}</td>
              <td>{{ props.item.customerEmail }}</td>
              <td>{{ props.item.creatorEmail }}</td>
              <td>{{ formatLineItems(props.item.lineItems) }}</td>
              <td>{{ $utils.humanFromVuetify(props.item.dateScheduled) }}</td>
              <td class="d-flex justify-end align-center">
                <v-tooltip location="top" :open-delay="500">
                  <template v-slot:activator="{ props: activatorProps }">
                    <v-icon size="small" class="mr-2" @click="viewQuote(props.item)" v-bind="activatorProps"
                      >mdi-clipboard-text</v-icon
                    >
                  </template>
                  <span>Original Quote</span>
                </v-tooltip>
                <v-tooltip location="top" :open-delay="500">
                  <template v-slot:activator="{ props: activatorProps }">
                    <v-icon size="small" class="mr-2" @click="packingView(props.item)" v-bind="activatorProps"
                      >mdi-clipboard-check</v-icon
                    >
                  </template>
                  <span>Packing List</span>
                </v-tooltip>
                <v-tooltip location="top" :open-delay="500">
                  <template v-slot:activator="{ props: activatorProps }">
                    <v-icon
                      :disabled="cloning"
                      size="small"
                      @click="
                        () => {
                          selectedShipment = props.item
                          cloneDialog = true
                        }
                      "
                      v-bind="activatorProps"
                      >mdi-content-copy</v-icon
                    >
                  </template>
                  <span>Clone to New Quote</span>
                </v-tooltip>
              </td>
            </tr>
          </template>
        </v-data-table>
      </div>
    </div>

    <!-- Component Overlay Dialog -->
    <v-dialog v-model="dialogVisible" fullscreen transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar color="primary" class="no-print elevation-0">
          <v-toolbar-title>{{ dialogTitle }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon
            @click="
              () => {
                dialogVisible = false
                overlayComponent = null
              }
            "
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <component
          :is="overlayComponent"
          :shipment="selectedShipment"
          :quoteID="selectedShipment.quoteID"
          :versionID="selectedShipment.versionID"
        ></component>
        <v-card-text> </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Clone Dialogue -->
    <v-dialog v-model="cloneDialog" width="500">
      <v-card>
        <v-card-title class="bg-error text-white text-h6"> Clone Quote </v-card-title>
        <v-card-text> Are you sure you want to clone this quote? </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="text" @click="cloneDialog = false">Cancel</v-btn>
          <v-btn color="accent" variant="flat" @click="cloneToQuote">Clone</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import QuoteEdit from '@/views/QuoteEdit'
import uid from 'uid'
import { auth, db } from '@/js/db'
import { addDoc, collection, doc, getDoc, onSnapshot, query, where } from 'firebase/firestore'

export default {
  name: 'Dispatched',
  components: { QuoteEdit },
  data() {
    return {
      tallyOrders: [],
      shipments: [],
      shipmentDetailsVisible: false,
      selectedShipment: {},
      loading: true,
      cloning: false,
      cloneDialog: false,
      headers: [
        {
          title: 'Order / Inv #',
          sortable: false,
          key: 'orderNumber'
        },
        {
          title: 'Customer',
          sortable: false,
          key: 'customerEmail'
        },
        {
          title: 'Creator',
          sortable: false,
          key: 'creatorEmail'
        },
        {
          title: 'Items',
          sortable: false,
          key: 'lineItems'
        },
        {
          title: 'Delivery Date',
          sortable: true,
          key: 'dateScheduled',
          sort: (a, b) => {
            const aDate = this.$utils.millisFromVuetify(a)
            const bDate = this.$utils.millisFromVuetify(b)
            return aDate < bDate ? -1 : 1
          }
        },
        {
          title: 'Actions',
          sortable: false,
          align: 'end',
          width: 160,
          key: null
        }
      ],
      tableSearch: '',
      sortConfig: {
        sortBy: 'dateScheduled',
        rowsPerPage: 10,
        descending: true
      },
      overlayComponent: null,
      dialogTitle: null,
      dialogVisible: false
    }
  },
  computed: {
    noDataText() {
      return this.loading ? '' : 'Nothing to display'
    },
    ukOrderTally() {
      let totalSum = 0
      for (const order of this.tallyOrders) {
        totalSum += order?.total
      }
      return 'Orders Totals = £' + this.$numeral(totalSum).format('0,0.00')
    }
  },
  methods: {
    formatLineItems(a) {
      var s = ''
      for (var i = 0; i < a.length; i++) {
        s += a[i]
        if (i < a.length - 1) s += ', '
      }
      return s
    },
    packingView(shipment) {
      var routeData = this.$router.resolve({
        name: 'packingView',
        params: { shipmentID: shipment.shipmentID }
      })
      window.open(routeData.href, '_blank')
    },
    viewQuote(shipment) {
      this.dialogVisible = true
      this.selectedShipment = shipment
      this.dialogTitle = shipment.customerEmail
      this.overlayComponent = 'QuoteEdit'
    },
    cloneToQuote(shipment) {
      this.cloning = true
      getDoc(doc(db, 'quotes', shipment.quoteID)).then((quoteSnap) => {
        var quote = quoteSnap.data()

        quote.creatorEmail = auth.currentUser.email
        quote.deadReason = null
        quote.orders = {}
        quote.dateCreated = this.$utils.tsNow()

        for (var i = 0; i < quote.versions.length; i++) {
          if (quote.versions[i].id == shipment.versionID) {
            var version = quote.versions[i]

            version.id = uid(10)
            version.published = false
            version.date = this.$utils.tsNow()

            quote.versions = [version]
          }
        }

        addDoc(collection(db, 'quotes'), quote).then((newQuoteRef) => {
          this.$router.push({
            name: 'quoteEdit',
            params: { quoteID: newQuoteRef.id }
          })
        })
      })
    }
  },
  async created() {
    await this.$live(
      'tallyOrders',
      query(collection(db, 'orders'), where('foc', '==', false), where('dispatched', '==', true))
      // where('numPayments', '>', 0)
    )
    // TODO if this bugs out might need to use $utils ensureFirestoreTimestamp on the date values
    this.unsubShipments = onSnapshot(
      query(collection(db, 'shipments'), where('shipped', '==', true)),
      (shipmentsQuery) => {
        this.loading = false
        var a = []
        shipmentsQuery.forEach((snap) => {
          var shipment = snap.data()
          shipment.shipmentID = snap.id
          shipment.dateScheduled = shipment.dateScheduled == null ? null : shipment.dateScheduled.toDate()
          shipment.cadFileSent = shipment.cadFileSent == null ? null : shipment.cadFileSent.toDate()
          shipment.materialOrdered = shipment.materialOrdered == null ? null : shipment.materialOrdered.toDate()
          shipment.completionDate = shipment.completionDate == null ? null : shipment.completionDate.toDate()
          shipment.sprayerDate = shipment.sprayerDate == null ? null : shipment.sprayerDate.toDate()
          shipment.startDate = shipment.startDate == null ? null : shipment.startDate.toDate()
          a.push(shipment)
        })
        this.shipments = a
      }
    )
  },
  beforeUnmount() {
    if (this.unsubShipments) this.unsubShipments()
  }
}
</script>
